/**
 * LotteryFlow Design System - Color Constants and Utilities
 * Story 2.4: Modern color scheme implementation
 */

// Color constants matching CSS custom properties
export const COLORS = {
  // Primary colors
  PRIMARY: '#0066CC',
  SUCCESS: '#007059',
  WARNING: '#FDCB6E',
  DANGER: '#C0392B',

  // Secondary colors
  ACCENT: '#74B9FF',
  TEXT_PRIMARY: '#2D3436',
  TEXT_SECONDARY: '#6C757D',
  BACKGROUND_SUBTLE: '#F8F9FA',
  BACKGROUND_PURE: '#FFFFFF',

  // Hover states
  PRIMARY_HOVER: '#0052A3',
  SUCCESS_HOVER: '#005A47',
  WARNING_HOVER: '#FCBF49',
  DANGER_HOVER: '#A6332A',
  ACCENT_HOVER: '#5DADE2',

  // Focus and disabled states
  FOCUS_RING: '#0052A3',
  FOCUS_RING_OFFSET: '#FFFFFF',
  DISABLED: '#E9ECEF',
  DISABLED_TEXT: '#ADB5BD',

  // Borders
  BORDER_LIGHT: '#DEE2E6',
  BORDER_MEDIUM: '#CED4DA',
} as const;

// RGB values for programmatic use
export const COLORS_RGB = {
  PRIMARY: [0, 102, 204],
  SUCCESS: [0, 112, 89],
  WARNING: [253, 203, 110],
  DANGER: [192, 57, 43],
  ACCENT: [116, 185, 255],
  TEXT_PRIMARY: [45, 52, 54],
  TEXT_SECONDARY: [108, 117, 125],
  BACKGROUND_SUBTLE: [248, 249, 250],
  BACKGROUND_PURE: [255, 255, 255],
} as const;

// HSL values for color manipulations
export const COLORS_HSL = {
  PRIMARY: [210, 100, 40],
  SUCCESS: [168, 100, 22],
  WARNING: [39, 97, 71],
  DANGER: [6, 63, 46],
  ACCENT: [210, 100, 73],
  TEXT_PRIMARY: [193, 9, 19],
  TEXT_SECONDARY: [208, 7, 46],
  BACKGROUND_SUBTLE: [210, 17, 98],
  BACKGROUND_PURE: [0, 0, 100],
} as const;

/**
 * Semantic color mappings for component usage
 */
export const SEMANTIC_COLORS = {
  // Trust and primary actions
  trust: COLORS.PRIMARY,
  reliability: COLORS.PRIMARY,

  // Success and positive states
  positive: COLORS.SUCCESS,
  confirmation: COLORS.SUCCESS,
  accuracy: COLORS.SUCCESS,

  // Warning and attention states
  attention: COLORS.WARNING,
  caution: COLORS.WARNING,
  moderate: COLORS.WARNING,

  // Error and danger states
  error: COLORS.DANGER,
  critical: COLORS.DANGER,
  risk: COLORS.DANGER,

  // Interactive elements
  interactive: COLORS.ACCENT,
  hover: COLORS.ACCENT,
  highlight: COLORS.ACCENT,
} as const;

/**
 * Component-specific color schemes
 */
export const COMPONENT_COLORS = {
  hero: {
    headline: COLORS.TEXT_PRIMARY,
    cta: COLORS.PRIMARY,
    ctaHover: COLORS.PRIMARY_HOVER,
    background: COLORS.BACKGROUND_SUBTLE,
    accent: COLORS.ACCENT,
  },

  demo: {
    success: COLORS.SUCCESS,
    data: COLORS.PRIMARY,
    interactive: COLORS.ACCENT,
    background: COLORS.BACKGROUND_SUBTLE,
  },

  testimonials: {
    text: COLORS.TEXT_PRIMARY,
    details: COLORS.TEXT_SECONDARY,
    background: COLORS.BACKGROUND_PURE,
    trust: COLORS.SUCCESS,
  },

  button: {
    primary: COLORS.PRIMARY,
    primaryHover: COLORS.PRIMARY_HOVER,
    success: COLORS.SUCCESS,
    successHover: COLORS.SUCCESS_HOVER,
    warning: COLORS.WARNING,
    warningHover: COLORS.WARNING_HOVER,
    danger: COLORS.DANGER,
    dangerHover: COLORS.DANGER_HOVER,
  },
} as const;

/**
 * Utility functions for color manipulations
 */

/**
 * Convert hex color to RGB array
 */
export function hexToRgb(hex: string): [number, number, number] | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/**
 * Calculate contrast ratio between two colors
 * Returns value between 1 and 21 (higher is better contrast)
 */
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = getLuminance(rgb2[0], rgb2[1], rgb2[2]);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Calculate relative luminance of a color
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Check if a color combination meets WCAG AA standards
 */
export function meetsWCAGAA(
  foreground: string,
  background: string,
  isLarge = false
): boolean {
  const ratio = getContrastRatio(foreground, background);
  return isLarge ? ratio >= 3 : ratio >= 4.5;
}

/**
 * Check if a color combination meets WCAG AAA standards
 */
export function meetsWCAGAAA(
  foreground: string,
  background: string,
  isLarge = false
): boolean {
  const ratio = getContrastRatio(foreground, background);
  return isLarge ? ratio >= 4.5 : ratio >= 7;
}

/**
 * Get appropriate text color (light or dark) for a given background
 */
export function getTextColor(backgroundColor: string): string {
  const whiteContrast = getContrastRatio('#FFFFFF', backgroundColor);
  const blackContrast = getContrastRatio('#000000', backgroundColor);

  return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
}

/**
 * Validate all brand color combinations for accessibility
 */
export function validateColorAccessibility() {
  const results = {
    passed: [] as Array<{ fg: string; bg: string; ratio: number }>,
    failed: [] as Array<{ fg: string; bg: string; ratio: number }>,
  };

  const backgrounds = [COLORS.BACKGROUND_PURE, COLORS.BACKGROUND_SUBTLE];
  const textColors = [COLORS.TEXT_PRIMARY, COLORS.TEXT_SECONDARY];
  const brandColors = [
    COLORS.PRIMARY,
    COLORS.SUCCESS,
    COLORS.WARNING,
    COLORS.DANGER,
  ];

  // Test text colors on backgrounds
  backgrounds.forEach(bg => {
    textColors.forEach(fg => {
      const ratio = getContrastRatio(fg, bg);
      const result = { fg, bg, ratio };

      if (meetsWCAGAA(fg, bg)) {
        results.passed.push(result);
      } else {
        results.failed.push(result);
      }
    });
  });

  // Test brand colors on backgrounds
  backgrounds.forEach(bg => {
    brandColors.forEach(fg => {
      const ratio = getContrastRatio(fg, bg);
      const result = { fg, bg, ratio };

      if (meetsWCAGAA(fg, bg)) {
        results.passed.push(result);
      } else {
        results.failed.push(result);
      }
    });
  });

  return results;
}

/**
 * CSS class name generators for consistent usage
 */
export const getColorClasses = {
  text: {
    primary: () => 'text-brand-primary',
    success: () => 'text-brand-success',
    warning: () => 'text-brand-warning',
    danger: () => 'text-brand-danger',
    accent: () => 'text-brand-accent',
    textPrimary: () => 'text-brand-text-primary',
    textSecondary: () => 'text-brand-text-secondary',
  },

  background: {
    primary: () => 'bg-brand-primary hover:bg-brand-primary-hover',
    success: () => 'bg-brand-success hover:bg-brand-success-hover',
    warning: () => 'bg-brand-warning hover:bg-brand-warning-hover',
    danger: () => 'bg-brand-danger hover:bg-brand-danger-hover',
    accent: () => 'bg-brand-accent hover:bg-brand-accent-hover',
    subtle: () => 'bg-brand-bg-subtle',
    pure: () => 'bg-brand-bg-pure',
  },

  border: {
    primary: () => 'border-brand-primary',
    success: () => 'border-brand-success',
    warning: () => 'border-brand-warning',
    danger: () => 'border-brand-danger',
    accent: () => 'border-brand-accent',
    light: () => 'border-brand-border-light',
    medium: () => 'border-brand-border-medium',
  },

  focus: () =>
    'focus:ring-2 focus:ring-brand-focus-ring focus:ring-offset-2 focus:ring-offset-brand-focus-ring-offset',
};
